// src/components/properties/RegistrationButton.jsx
import { Button, OverlayTrigger, Row, Tooltip, Col, Image } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRegistered } from "@fortawesome/free-solid-svg-icons";
import { FilePlus } from "lucide-react";
import { BsLightningFill } from "react-icons/bs";
import {ReactComponent as ZeusLogo } from "../../images/zeus_bolt_black_outline.svg";
import { isOnlineOrZeus, isRegistrationOpen, getRegistrationStatus } from "../../utilities/hybridSales";

/**
 * Renders one of four registration button variants depending on the
 * property's auction type:
 *   1. Hybrid sale  → "Online Reg / In-Person Sale" (links to zeusauction.com)
 *   2. Online sale  → "Register: Zeus"              (links to zeusauction.com)
 *   3. Foreclosure without Zeus link → null (no registration available)
 *   4. Standard     → toggleable Register / Unregister button
 *
 * All buttons use a stacked icon-on-top, text-on-bottom layout for uniform height.
 *
 * @param {object}        property
 * @param {boolean}       isRegistered
 * @param {number|string} standardizedSaleId
 * @param {number|string} effectiveSaleId     - real auction sale ID for foreclosures
 * @param {boolean}       isFetchingInfo  - true while propertyInfo is loading
 * @param {object}        propertyInfo    - truthy once card details have loaded
 * @param {function}      onRegisterClick - called with { saleId, auction }
 * @param {function}      onError         - called with an error message string
 */

const btnContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
};

const RegistrationButton = ({
  property,
  isRegistered,
  standardizedSaleId,
  effectiveSaleId,
  isFetchingInfo,
  propertyInfo,
  isCancelled,
  onRegisterClick,
  onError,
}) => {
  const isHybrid = isOnlineOrZeus(property) === "Online Registration/In-Person Sale";
  // Match Auction List: Online type OR Zeus in location (see hybridSales.isOnlineOrZeus)
  const isOnlineAuction = isOnlineOrZeus(property) === "Online/Zeus";

  if (isHybrid) {
    const auctionDetail = property.auctionDetail;
    const registrationStatus = getRegistrationStatus(auctionDetail);
    const registrationOpen = isRegistrationOpen(auctionDetail);

    const tooltip = isCancelled ? (
      <Tooltip>This sale has been cancelled.</Tooltip>
    ) : registrationStatus === "closed" ? (
      <Tooltip>Registration for this sale has closed.</Tooltip>
    ) : registrationStatus === "not_open" ? (
      <Tooltip>Registration for this sale has not yet opened.</Tooltip>
    ) : (
      <span />
    );

    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <span className="unique-auction-button-wrapper flex-fill">
          <Button
            variant="outline-primary"
            href={isCancelled ? undefined : "http://www.zeusauction.com"}
            target={isCancelled ? undefined : "_blank"}
            rel={isCancelled ? undefined : "noopener noreferrer"}
            className="unique-auction-action-button unique-auction-register-zeus-button btn-sm w-100 align-items-center justify-content-start gap-3 px-2"
            disabled={isCancelled || !registrationOpen}
            aria-label="Register online for in-person sale via Zeus"
          >
            <FilePlus size={20} />
              <span>Online Reg/</span>
              <span>In-Person Sale</span>
          </Button>
        </span>
      </OverlayTrigger>
    );
  }

  if (isOnlineAuction) {
    return (
      <Button
        className="d-flex direction-column align-items-center justify-content-start gap-3 w-100 px-2"
        variant="outline-primary"
        href={isCancelled ? undefined : "http://www.zeusauction.com"}
        target={isCancelled ? undefined : "_blank"}
        rel={isCancelled ? undefined : "noopener noreferrer"}
        disabled={isCancelled || !isRegistrationOpen(property.auctionDetail)}
        aria-label="Register for Zeus online auction"
      >
        <ZeusLogo width={20} height={20} />
        <span>Register</span>

      </Button>
    );
  }

  // Foreclosure sales without a Zeus link cannot pre-register
  if (property.saleType === "F") {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>This foreclosure sale cannot be pre-registered for.</Tooltip>}>
        <span className="unique-auction-button-wrapper flex-fill">
          <Button
            className="d-flex direction-column align-items-center justify-content-start w-100 cardButton"
            disabled
            aria-label="This foreclosure sale cannot be pre-registered for"
          >
            <span style={{ fontSize: "0.875rem" }}>No Registration</span>
          </Button>
        </span>
      </OverlayTrigger>
    );
  }

  return (
    <Button
      className="d-flex direction-column align-items-center justify-content-start gap-3 w-100 px-2"
      variant={isRegistered ? "outline-danger" : "outline-primary"}
      onClick={() => {
        const saleIdToUse = effectiveSaleId ?? standardizedSaleId;
        if (saleIdToUse === null || saleIdToUse === undefined || isNaN(Number(saleIdToUse))) {
          onError("Cannot process your request due to an invalid sale ID.");
          return;
        }
        onRegisterClick({ saleId: saleIdToUse, auction: property });
      }}
      disabled={isCancelled || isFetchingInfo || !propertyInfo}
      aria-label={isRegistered ? "Unregister from this sale" : "Register for this sale"}
    >
      <FilePlus size={20} />
      <span>{isRegistered ? "Unregister" : "Register"}</span>
    </Button>
  );
};

export default RegistrationButton;
